(ns app.front.view.property.v2.relations-section
  (:require [app.domain.util :as du]
            [app.front.ui :as ui]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.ui.sortable-table :as sortable-table]
            [app.front.view.property.subs :as property-subs]
            [app.front.view.property.util :as property-util]
            [app.front.view.property.v2.merge-modal :as merge-modal]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [re-frame.core-instrumented :as rf]))

(defn can-modify-relations? [property]
  (let [can-write-property? (-> property :granted-permissions :permission.property/write)
        management-group?   (<sub [::property-subs/property-management-group?])
        editing?            (<sub [::property-subs/editing?])]
    (and management-group? can-write-property? editing?
         (du/in-state? :property.state/active property))))

(defn can-merge-properties? [property]
  (let [can-modify?     (can-modify-relations? property)
        uses-system-db? (<sub [::property-subs/property-uses-system-database?])]
    (and can-modify? uses-system-db?)))

(defn subdivision-modal [tr parent-property]
  (let [form-data      (<sub [::property-subs/subdivision-form-data])
        property-id    (:property-id form-data)
        parent-address (get-in parent-property [:property/primary-address :address/full-address])]
    [ui-modal/composed-input-modal
     {:open       true
      :class-name "subdivision-modal"
      :on-close   #(rf/dispatch [::property-subs/show-subdivision-modal false])}

     (cc/modal-header
       {:close-modal #(rf/dispatch [::property-subs/show-subdivision-modal false])
        :title       (tr [:property-view/create-subdivision-property])})

     (cc/modal-body
       [:div.space-y-4
        [:p (str (tr [:property-view/creating-subdivision-from]) " "
                 (or (:property/id parent-property)
                     (str (tr [:property-view/property]) " " (:db/id parent-property))))]

        (when parent-address
          [:div
           [:label.block.text-sm.font-medium.mb-2 (tr [:property-view/address])]
           [:div.text-sm.text-gray-600 parent-address]])

        [:div
         [:label.block.text-sm.font-medium.mb-2
          (str (tr [:property/new-property-record-id-text] [(tr [:property/id])]) " (" (tr [:optional]) ")")]
         [ui/text-input
          {:value       (or property-id "")
           :placeholder ""
           :on-change   #(rf/dispatch [::property-subs/set-subdivision-form-data
                                       :property-id (.. % -target -value)])}]]])

     (cc/modal-footer
       [ui/button
        {:kind     :secondary
         :on-click #(rf/dispatch [::property-subs/show-subdivision-modal false])}
        (tr [:cancel])]

       [ui/button
        {:kind     :primary
         :on-click #(rf/dispatch [::property-subs/create-subdivision-property])}
        (tr [:property-view/create-subdivision-property])])]))

(defn merge-properties-button [tr property]
  (when (can-merge-properties? property)
    [ui/button
     {:kind     :primary
      :size     :small
      :on-click #(rf/dispatch [::property-subs/show-merge-modal true])}
     (tr [:property-view/merge-properties])]))

(defn subdivide-property-button [tr property]
  (when (can-modify-relations? property)
    [ui/button
     {:kind     :primary
      :size     :small
      :on-click #(rf/dispatch [::property-subs/show-subdivision-modal true])}
     (tr [:property-view/subdivide-property])]))

(defn relations-section [{:keys [tr property]}]
  (let [format-date        (<sub [::date-util/date-fn])
        latest-change      (:latest-change property)
        created            (:entity/created property)
        relations          (<sub [::property-subs/property-relations])
        loading?           (<sub [::property-subs/property-relations-loading?])
        selected-relations (<sub [::property-subs/selected-relations])
        has-selected?      (<sub [::property-subs/has-selected-relations?])]

    [:div.space-y-6
     [:div.space-y-3
      (when created
        [:div.text-sm.text-gray-900
         [:span.font-medium.text-gray-700 (tr [:property-view/date-created])]
         " "
         [:span (format-date created)]])
      (when latest-change
        [:div.text-sm.text-gray-900
         [:span.font-medium.text-gray-700 (tr [:property-view/date-modified])]
         " "
         [:span (-> latest-change :ts format-date)]])]
     [:div
      [:h4.text-lg.font-semibold.mb-2 (tr [:property-view/property-relations])]
      [:div.flex.gap-2
       [merge-properties-button tr property]
       [subdivide-property-button tr property]]]

     ;; State change dropdown
     (when (and (can-modify-relations? property) (seq relations))
       [:div.flex.gap-2.mb-4
        [:div.flex.flex-col.gap-1
         [:label.text-sm.font-medium.text-gray-700 (tr [:property-view/change-selected-state])]
         [ui-select/select {:id           "state-change-select"
                            :label-text   (tr [:property-view/change-selected-state])
                            :hide-label   true
                            :allow-empty? true
                            :placeholder  (tr [:property-view/select-state-action])
                            :disabled     (not has-selected?)
                            :values       [:property-view/set-to-active
                                           :property-view/set-to-historical]
                            :on-change    (fn [selection]
                                            (when selection
                                              (when (seq selected-relations)
                                                (case selection
                                                  :property-view/set-to-active     (rf/dispatch [::property-subs/set-properties-to-active (vec selected-relations)])
                                                  :property-view/set-to-historical (rf/dispatch [::property-subs/set-properties-to-historical (vec selected-relations)])))))
                            :value        nil
                            :light        true
                            :val->str     #(tr [%])}]]])

     ;; Relations table
     (when (seq relations)
       [sortable-table/table
        {:id                "property-relations"
         :cols              [(when (can-modify-relations? property)
                               [""
                                #(let [entity-id (:entity-id %)]
                                   [cc/checkbox
                                    {:id         (str "select_" entity-id)
                                     :label-text (tr [:select/select])
                                     :hide-label true
                                     :checked    (contains? selected-relations entity-id)
                                     :on-change  (fn [checked?]
                                                   (rf/dispatch [::property-subs/toggle-relation-selection
                                                                 entity-id checked?]))}])
                                {:flex 0 :min-width "2rem"}])
                             [(tr [:property-view/relation])
                              #(tr [(:relation %)])
                              {}]
                             [(tr [:property-view/date-modified])
                              #(if (:date-modified %)
                                 (format-date (:date-modified %))
                                 "-")
                              {}]
                             [(tr [:property-view/address])
                              #(let [address (or (:address %) "-")
                                     state   (:state %)]
                                 [:div.flex.items-center.justify-between
                                  [:span address]
                                  (when (= state :property.state/historical)
                                    [ui/tag {:color      :blue
                                             :type       :round
                                             :class-name "bg-cyan-400 text-black"}
                                     (-> (tr [:property.state/historical]) str/upper-case)])])
                              {}]]
         :empty-placeholder (if loading?
                              [cc/loading-spinner {:with-overlay false}]
                              (tr [:property-view/no-relations]))
         :id-func           :property-id
         ;; Carry this property as `property-ref` so the sub-header's back button returns here
         ;; instead of falling back to the property dashboard, see `property-subs/back-href`.
         :link              {:make-href #(property-util/property-link
                                           {:db/id (:entity-id %)}
                                           {:property-ref (:db/id property)})}
         :items             relations
         :tr                tr}])

     (when (<sub [::property-subs/subdivision-modal-open?])
       [subdivision-modal tr property])

     (when (<sub [::property-subs/merge-modal-open?])
       [merge-modal/merge-modal tr property])]))
